import { useReducer, useCallback } from 'react';
import { calculateItemTotal } from '../utils';

/**
 * Invoice state actions
 */
export const INVOICE_ACTIONS = {
    // Invoice Items
    ADD_ITEM: 'ADD_ITEM',
    REMOVE_ITEM: 'REMOVE_ITEM',
    UPDATE_ITEM: 'UPDATE_ITEM',
    SET_ITEMS: 'SET_ITEMS',
    MARK_ITEM_FOR_DELETION: 'MARK_ITEM_FOR_DELETION',
    UNMARK_ITEM_FOR_DELETION: 'UNMARK_ITEM_FOR_DELETION',

    // Payments
    ADD_PAYMENT: 'ADD_PAYMENT',
    REMOVE_PAYMENT: 'REMOVE_PAYMENT',
    UPDATE_PAYMENT: 'UPDATE_PAYMENT',
    SET_PAYMENTS: 'SET_PAYMENTS',
    MARK_PAYMENT_FOR_DELETION: 'MARK_PAYMENT_FOR_DELETION',
    UNMARK_PAYMENT_FOR_DELETION: 'UNMARK_PAYMENT_FOR_DELETION',

    // Discounts
    ADD_DISCOUNT: 'ADD_DISCOUNT',
    REMOVE_DISCOUNT: 'REMOVE_DISCOUNT',
    UPDATE_DISCOUNT: 'UPDATE_DISCOUNT',
    SET_DISCOUNTS: 'SET_DISCOUNTS',
    MARK_DISCOUNT_FOR_DELETION: 'MARK_DISCOUNT_FOR_DELETION',
    UNMARK_DISCOUNT_FOR_DELETION: 'UNMARK_DISCOUNT_FOR_DELETION',

    // Insurance Claims
    ADD_CLAIM: 'ADD_CLAIM',
    REMOVE_CLAIM: 'REMOVE_CLAIM',
    UPDATE_CLAIM: 'UPDATE_CLAIM',
    SET_CLAIMS: 'SET_CLAIMS',
    MARK_CLAIM_FOR_DELETION: 'MARK_CLAIM_FOR_DELETION',
    UNMARK_CLAIM_FOR_DELETION: 'UNMARK_CLAIM_FOR_DELETION',

    // Invoice Meta
    SET_INVOICE_META: 'SET_INVOICE_META',
    UPDATE_CUSTOMER: 'UPDATE_CUSTOMER',

    // Refund
    ADD_REFUND: 'ADD_REFUND',
    REMOVE_REFUND: 'REMOVE_REFUND',
    UPDATE_REFUND: 'UPDATE_REFUND',
    SET_REFUNDS: 'SET_REFUNDS',
    MARK_REFUND_FOR_DELETION: 'MARK_REFUND_FOR_DELETION',
    UNMARK_REFUND_FOR_DELETION: 'UNMARK_REFUND_FOR_DELETION',

    // Credit Memos
    ADD_CREDIT_MEMO: 'ADD_CREDIT_MEMO',
    REMOVE_CREDIT_MEMO: 'REMOVE_CREDIT_MEMO',
    UPDATE_CREDIT_MEMO: 'UPDATE_CREDIT_MEMO',
    SET_CREDIT_MEMOS: 'SET_CREDIT_MEMOS',
    MARK_CREDIT_MEMO_FOR_DELETION: 'MARK_CREDIT_MEMO_FOR_DELETION',
    UNMARK_CREDIT_MEMO_FOR_DELETION: 'UNMARK_CREDIT_MEMO_FOR_DELETION',

    // Full Reset
    RESET_INVOICE: 'RESET_INVOICE',
    SET_FULL_INVOICE: 'SET_FULL_INVOICE'
};

/**
 * Initial state for invoice
 */
const initialInvoiceState = {
    invoiceId: null,
    customerId: null,
    invoiceNumber: null,
    invoiceDate: null,
    dueDate: null,
    status: 'draft',
    notes: '',
    editFrame: false,
    editingFrameItem: null,
    // Invoice components
    invoiceItems: [],
    payments: [],
    discounts: [],
    claims: [],
    refunds: [],
    creditMemos: [],

    // Customer info (denormalized for convenience)
    customer: null,

    // Transfer fields
    transferredFrom: null,
    transferredTo: null
};

/**
 * Reducer function for managing complex invoice state
 */
function invoiceReducer(state, action) {
    switch (action.type) {
        // Invoice Items
        case INVOICE_ACTIONS.ADD_ITEM:
            // Ensure item has a total calculated initially
            const newItem = { ...action.payload, total: calculateItemTotal(action.payload) };
            return {
                ...state,
                invoiceItems: [...state.invoiceItems, newItem],
                // selectTreatment: action.selectTreatment !== undefined ? action.selectTreatment : state.selectTreatment,
                // selectAccessory: action.selectAccessory !== undefined ? action.selectAccessory : state.selectAccessory,
                // selectFrame: action.selectFrame !== undefined ? action.selectFrame : state.selectFrame,
                // selectLens: action.selectLens !== undefined ? action.selectLens : state.selectLens,
            };
        // return {
        //     ...state,
        //     invoiceItems: [...state.invoiceItems, { ...action.payload }]
        // };

        case INVOICE_ACTIONS.REMOVE_ITEM:

            return {
                ...state,
                invoiceItems: state.invoiceItems.filter(item => item.id !== action.payload.id)
            };

        case INVOICE_ACTIONS.UPDATE_ITEM:
            const updatedItems = state.invoiceItems.map(item => {
                // Use the unique 'id' for comparison
                if (item.id === action.payload.item.id) {
                    // If updatedItem is provided, use it directly, otherwise update single field
                    if (action.payload.updatedItem) {
                        return action.payload.updatedItem;
                    } else {
                        const updatedItem = { ...item, [action.payload.field]: action.payload.value };
                        // Ensure calculateItemTotal uses the fully updated item
                        updatedItem.total = calculateItemTotal(updatedItem);
                        return updatedItem;
                    }
                }
                return item;
            });
            return { ...state, invoiceItems: updatedItems };

        // return {
        //     ...state,
        //     invoiceItems: state.invoiceItems.map(item =>
        //         item.id === action.payload.id ? { ...item, ...action.payload.updates } : item
        //     )
        // };

        case INVOICE_ACTIONS.SET_ITEMS:
            return {
                ...state,
                invoiceItems: action.payload
            };

        case INVOICE_ACTIONS.MARK_ITEM_FOR_DELETION:
            return {
                ...state,
                invoiceItems: state.invoiceItems.map(item =>
                    item.id === action.payload.id ? { ...item, _markedForDeletion: true } : item
                )
            };

        case INVOICE_ACTIONS.UNMARK_ITEM_FOR_DELETION:
            return {
                ...state,
                invoiceItems: state.invoiceItems.map(item =>
                    item.id === action.payload.id ? { ...item, _markedForDeletion: false } : item
                )
            };

        // Payments
    //         case 'DELETE_PAYMENT':     
        case INVOICE_ACTIONS.ADD_PAYMENT:
            return { ...state, payments: [...state.payments, action.payload] };

        case INVOICE_ACTIONS.REMOVE_PAYMENT:
            const filteredPayments = state.payments.filter((p) => {
                // Keep payments that DON'T match the id/tempId being removed
                const matchesId = p.id && p.id === action.payload.id;
                const matchesTempId = p.tempId && p.tempId === action.payload.id;
                return !(matchesId || matchesTempId);
            });
            return {
                ...state,
                payments: filteredPayments
            };  
            /*            
            return {
                ...state,
                payments: state.payments.filter(payment => payment.id !== action.payload.id)
            };*/

        case INVOICE_ACTIONS.UPDATE_PAYMENT:

            const updatedPaymentItems = state.payments.map((p) => {
                const matchById = p.id && p.id === action.payload.id;
                const matchByTempId = p.tempId && p.tempId === action.payload.tempId;
                return matchById || matchByTempId ? action.payload : p;
            });
            return {
                ...state,
                payments: updatedPaymentItems
            };

        case INVOICE_ACTIONS.SET_PAYMENTS:
            return {
                ...state,
                payments: action.payload
            };

        case INVOICE_ACTIONS.MARK_PAYMENT_FOR_DELETION:
            return {
                ...state,
                payments: state.payments
                    .filter(payment => {
                        // If it's a temp payment (no real id), remove it immediately
                        const isTarget = payment.id === action.payload.id || payment.tempId === action.payload.id;
                        if (isTarget && payment.tempId && !payment.id) {
                            return false; // Remove temp payment
                        }
                        return true; // Keep all others
                    })
                    .map(payment => {
                        // Mark persisted payments for deletion
                        const isTarget = payment.id === action.payload.id || payment.tempId === action.payload.id;
                        return isTarget ? { ...payment, _markedForDeletion: true } : payment;
                    })
            };

        case INVOICE_ACTIONS.UNMARK_PAYMENT_FOR_DELETION:
            return {
                ...state,
                payments: state.payments.map(payment =>
                    (payment.id === action.payload.id || payment.tempId === action.payload.id)
                        ? { ...payment, _markedForDeletion: false }
                        : payment
                )
            };

        // Discounts
        case INVOICE_ACTIONS.ADD_DISCOUNT:
            return { ...state, discounts: [...state.discounts, action.payload] };
        //   return {
        //     ...state,
        //     discounts: [...state.discounts, { ...action.payload, id: Date.now() }]
        //   };

        case INVOICE_ACTIONS.REMOVE_DISCOUNT:
            const filteredDiscounts = state.discounts.filter((d) => {
                // Keep discounts that DON'T match the id/tempId being removed
                const matchesId = d.id && d.id === action.payload.id;
                const matchesTempId = d.tempId && d.tempId === action.payload.id;
                return !(matchesId || matchesTempId);
            });
            return {
                ...state,
                discounts: filteredDiscounts
            };

        case INVOICE_ACTIONS.UPDATE_DISCOUNT:
            return {
                ...state,
                discounts: state.discounts.map(discount => {
                    const matchById = discount.id && discount.id === action.payload.id;
                    const matchByTempId = discount.tempId && discount.tempId === action.payload.tempId;
                    return (matchById || matchByTempId) ? action.payload : discount;
                })
            };

        case INVOICE_ACTIONS.SET_DISCOUNTS:
            return {
                ...state,
                discounts: action.payload
            };

        case INVOICE_ACTIONS.MARK_DISCOUNT_FOR_DELETION:
            return {
                ...state,
                discounts: state.discounts
                    .filter(discount => {
                        // If it's a temp discount (no real id), remove it immediately
                        const isTarget = discount.id === action.payload.id || discount.tempId === action.payload.id;
                        if (isTarget && discount.tempId && !discount.id) {
                            return false; // Remove temp discount
                        }
                        return true; // Keep all others
                    })
                    .map(discount => {
                        // Mark persisted discounts for deletion
                        const isTarget = discount.id === action.payload.id || discount.tempId === action.payload.id;
                        return isTarget ? { ...discount, _markedForDeletion: true } : discount;
                    })
            };

        case INVOICE_ACTIONS.UNMARK_DISCOUNT_FOR_DELETION:
            console.log('UNMARK_DISCOUNT_FOR_DELETION action payload:', action.payload);
            return {
                ...state,
                discounts: state.discounts.map(discount =>
                    (discount.id === action.payload.id || discount.tempId === action.payload.id)
                        ? { ...discount, _markedForDeletion: false }
                        : discount
                )
            };

        // Insurance Claims
        case INVOICE_ACTIONS.ADD_CLAIM:
            return {
                ...state,
                claims: [...state.claims, { ...action.payload, id: Date.now() }]
            };

        case INVOICE_ACTIONS.REMOVE_CLAIM:
            return {
                ...state,
                claims: state.claims.filter(claim => claim.id !== action.payload.id)
            };

        case INVOICE_ACTIONS.UPDATE_CLAIM:
            const updatedClaimItems = state.claims.map((c) => {
                const matchById = c.id && c.id === action.payload.id;
                const matchByTempId = c.tempId && c.tempId === action.payload.tempId;
                return matchById || matchByTempId ? action.payload : c;
            });
            return {
                ...state,
                claims: updatedClaimItems
            };

        case INVOICE_ACTIONS.SET_CLAIMS:
            return {
                ...state,
                claims: action.payload
            };

        case INVOICE_ACTIONS.MARK_CLAIM_FOR_DELETION:
            return {
                ...state,
                claims: state.claims
                    .filter(claim => {
                        // If it's a temp claim (no real id), remove it immediately
                        const isTarget = claim.id === action.payload.id || claim.tempId === action.payload.id;
                        if (isTarget && claim.tempId && !claim.id) {
                            return false; // Remove temp claim
                        }
                        return true; // Keep all others
                    })
                    .map(claim => {
                        // Mark persisted claims for deletion
                        const isTarget = claim.id === action.payload.id || claim.tempId === action.payload.id;
                        return isTarget ? { ...claim, _markedForDeletion: true } : claim;
                    })
            };

        case INVOICE_ACTIONS.UNMARK_CLAIM_FOR_DELETION:
            return {
                ...state,
                claims: state.claims.map(claim =>
                    (claim.id === action.payload.id || claim.tempId === action.payload.id)
                        ? { ...claim, _markedForDeletion: false }
                        : claim
                )
            };

        // Refunds
        case INVOICE_ACTIONS.ADD_REFUND:
            return {
                ...state,
                refunds: [...state.refunds, action.payload]
            };

        case INVOICE_ACTIONS.REMOVE_REFUND:
            return {
                ...state,
                refunds: state.refunds.filter(refund => refund.id !== action.payload.id)
            };

        case INVOICE_ACTIONS.UPDATE_REFUND:
            const updatedRefunds = state.refunds.map((r) => {
                const matchById = r.id && r.id === action.payload.id;
                const matchByTempId = r.tempId && r.tempId === action.payload.tempId;
                return matchById || matchByTempId ? action.payload : r;
            });
            return { ...state, refunds: updatedRefunds };
        // return {
        //     ...state,
        //     refunds: state.refunds.map(refund =>
        //         refund.id === action.payload.id ? { ...refund, ...action.payload.updates } : refund
        //     )
        // };
        case INVOICE_ACTIONS.SET_REFUNDS:
            return {
                ...state,
                refunds: action.payload
            };

        case INVOICE_ACTIONS.MARK_REFUND_FOR_DELETION:
            return {
                ...state,
                refunds: state.refunds
                    .filter(refund => {
                        // If it's a temp refund (no real id), remove it immediately
                        const isTarget = refund.id === action.payload.id || refund.tempId === action.payload.id;
                        if (isTarget && refund.tempId && !refund.id) {
                            return false; // Remove temp refund
                        }
                        return true; // Keep all others
                    })
                    .map(refund => {
                        // Mark persisted refunds for deletion
                        const isTarget = refund.id === action.payload.id || refund.tempId === action.payload.id;
                        return isTarget ? { ...refund, _markedForDeletion: true } : refund;
                    })
            };

        case INVOICE_ACTIONS.UNMARK_REFUND_FOR_DELETION:
            return {
                ...state,
                refunds: state.refunds.map(refund =>
                    (refund.id === action.payload.id || refund.tempId === action.payload.id)
                        ? { ...refund, _markedForDeletion: false }
                        : refund
                )
            };

        // Credit Memos
        case INVOICE_ACTIONS.ADD_CREDIT_MEMO:
            return {
                ...state,
                creditMemos: [...state.creditMemos, action.payload]
            };

        case INVOICE_ACTIONS.REMOVE_CREDIT_MEMO:
            return {
                ...state,
                creditMemos: state.creditMemos.filter(memo => memo.id !== action.payload.id)
            };

        case INVOICE_ACTIONS.UPDATE_CREDIT_MEMO:
            const updatedCreditMemos = state.creditMemos.map((m) => {
                const matchById = m.id && m.id === action.payload.id;
                const matchByTempId = m.tempId && m.tempId === action.payload.tempId;
                return matchById || matchByTempId ? action.payload : m;
            });
            return { ...state, creditMemos: updatedCreditMemos };

        case INVOICE_ACTIONS.SET_CREDIT_MEMOS:
            return {
                ...state,
                creditMemos: action.payload
            };

        case INVOICE_ACTIONS.MARK_CREDIT_MEMO_FOR_DELETION:
            return {
                ...state,
                creditMemos: state.creditMemos
                    .filter(memo => {
                        // If it's a temp credit memo (no real id), remove it immediately
                        const isTarget = memo.id === action.payload.id || memo.tempId === action.payload.id;
                        if (isTarget && memo.tempId && !memo.id) {
                            return false; // Remove temp credit memo
                        }
                        return true; // Keep all others
                    })
                    .map(memo => {
                        // Mark persisted credit memos for deletion
                        const isTarget = memo.id === action.payload.id || memo.tempId === action.payload.id;
                        return isTarget ? { ...memo, _markedForDeletion: true } : memo;
                    })
            };

        case INVOICE_ACTIONS.UNMARK_CREDIT_MEMO_FOR_DELETION:
            return {
                ...state,
                creditMemos: state.creditMemos.map(memo =>
                    (memo.id === action.payload.id || memo.tempId === action.payload.id)
                        ? { ...memo, _markedForDeletion: false }
                        : memo
                )
            };

        // Invoice Meta
        case INVOICE_ACTIONS.SET_INVOICE_META:
            return {
                ...state,
                ...action.payload
            };

        case INVOICE_ACTIONS.UPDATE_CUSTOMER:
            return {
                ...state,
                customer: action.payload,
                customerId: action.payload?.id || null
            };

        // Full Reset/Set
        case INVOICE_ACTIONS.RESET_INVOICE:
            return { ...initialInvoiceState };

        case INVOICE_ACTIONS.SET_FULL_INVOICE:
            return {
                ...initialInvoiceState,
                ...action.payload
            };

        default:
            console.warn(`Unknown action type: ${action.type}`);
            return state;
    }
}

/**
 * Custom hook for managing invoice state with complex reducer logic
 * 
 * This hook encapsulates all the state management logic for an invoice,
 * including items, payments, discounts, claims, and metadata.
 * 
 * @param {Object} initialData - Initial invoice data to populate the state
 * @returns {Object} State and action creators for managing the invoice
 */
export const useInvoiceState = (initialData = {}) => {
    const [invoiceState, dispatch] = useReducer(invoiceReducer, {
        ...initialInvoiceState,
        ...initialData
    });

    // Action creators
    const actions = {
        // Invoice Items
        addItem: useCallback((item) => {
            dispatch({ type: INVOICE_ACTIONS.ADD_ITEM, payload: item });
        }, []),

        removeItem: useCallback((item) => {
            dispatch({ type: INVOICE_ACTIONS.REMOVE_ITEM, payload: { id: item.id } });
        }, []),

        updateItem: useCallback((updates) => {
            dispatch({ type: INVOICE_ACTIONS.UPDATE_ITEM, payload: updates });
        }, []),

        setItems: useCallback((items) => {
            dispatch({ type: INVOICE_ACTIONS.SET_ITEMS, payload: items });
        }, []),

        markItemForDeletion: useCallback((id) => {
            dispatch({ type: INVOICE_ACTIONS.MARK_ITEM_FOR_DELETION, payload: { id } });
        }, []),

        unmarkItemForDeletion: useCallback((id) => {
            dispatch({ type: INVOICE_ACTIONS.UNMARK_ITEM_FOR_DELETION, payload: { id } });
        }, []),

        // Payments
        addPayment: useCallback((payment) => {
            dispatch({ type: INVOICE_ACTIONS.ADD_PAYMENT, payload: payment });
        }, []),

        removePayment: useCallback((id) => {
            dispatch({ type: INVOICE_ACTIONS.REMOVE_PAYMENT, payload: { id } });
        }, []),

        updatePayment: useCallback((payment) => {
            dispatch({ type: INVOICE_ACTIONS.UPDATE_PAYMENT, payload: payment });
        }, []),

        setPayments: useCallback((payments) => {
            dispatch({ type: INVOICE_ACTIONS.SET_PAYMENTS, payload: payments });
        }, []),

        markPaymentForDeletion: useCallback((id) => {
            dispatch({ type: INVOICE_ACTIONS.MARK_PAYMENT_FOR_DELETION, payload: { id } });
        }, []),

        unmarkPaymentForDeletion: useCallback((id) => {
            dispatch({ type: INVOICE_ACTIONS.UNMARK_PAYMENT_FOR_DELETION, payload: { id } });
        }, []),

        // Discounts
        addDiscount: useCallback((discount) => {
            dispatch({ type: INVOICE_ACTIONS.ADD_DISCOUNT, payload: discount });
        }, []),

        removeDiscount: useCallback((id) => {
            dispatch({ type: INVOICE_ACTIONS.REMOVE_DISCOUNT, payload: { id } });
        }, []),

        updateDiscount: useCallback((discount) => {
            dispatch({ type: INVOICE_ACTIONS.UPDATE_DISCOUNT, payload: discount });
        }, []),

        setDiscounts: useCallback((discounts) => {
            dispatch({ type: INVOICE_ACTIONS.SET_DISCOUNTS, payload: discounts });
        }, []),

        markDiscountForDeletion: useCallback((id) => {
            dispatch({ type: INVOICE_ACTIONS.MARK_DISCOUNT_FOR_DELETION, payload: { id } });
        }, []),

        unmarkDiscountForDeletion: useCallback((id) => {
            dispatch({ type: INVOICE_ACTIONS.UNMARK_DISCOUNT_FOR_DELETION, payload: { id } });
        }, []),

        // Insurance Claims
        addClaim: useCallback((claim) => {
            dispatch({ type: INVOICE_ACTIONS.ADD_CLAIM, payload: claim });
        }, []),

        removeClaim: useCallback((id) => {
            dispatch({ type: INVOICE_ACTIONS.REMOVE_CLAIM, payload: { id } });
        }, []),

        updateClaim: useCallback((claim) => {
            dispatch({ type: INVOICE_ACTIONS.UPDATE_CLAIM, payload: claim });
        }, []),

        setClaims: useCallback((claims) => {
            dispatch({ type: INVOICE_ACTIONS.SET_CLAIMS, payload: claims });
        }, []),

        markClaimForDeletion: useCallback((id) => {
            dispatch({ type: INVOICE_ACTIONS.MARK_CLAIM_FOR_DELETION, payload: { id } });
        }, []),

        unmarkClaimForDeletion: useCallback((id) => {
            dispatch({ type: INVOICE_ACTIONS.UNMARK_CLAIM_FOR_DELETION, payload: { id } });
        }, []),

        // Refunds
        addRefund: useCallback((refund) => {
            dispatch({ type: INVOICE_ACTIONS.ADD_REFUND, payload: refund });
        }, []),

        removeRefund: useCallback((id) => {
            dispatch({ type: INVOICE_ACTIONS.REMOVE_REFUND, payload: { id } });
        }, []),

        updateRefund: useCallback((refund) => {
            dispatch({ type: INVOICE_ACTIONS.UPDATE_REFUND, payload: refund });
        }, []),

        setRefunds: useCallback((refunds) => {
            dispatch({ type: INVOICE_ACTIONS.SET_REFUNDS, payload: refunds });
        }, []),

        markRefundForDeletion: useCallback((id) => {
            dispatch({ type: INVOICE_ACTIONS.MARK_REFUND_FOR_DELETION, payload: { id } });
        }, []),

        unmarkRefundForDeletion: useCallback((id) => {
            dispatch({ type: INVOICE_ACTIONS.UNMARK_REFUND_FOR_DELETION, payload: { id } });
        }, []),

        // Credit Memos
        addCreditMemo: useCallback((memo) => {
            dispatch({ type: INVOICE_ACTIONS.ADD_CREDIT_MEMO, payload: memo });
        }, []),

        removeCreditMemo: useCallback((id) => {
            dispatch({ type: INVOICE_ACTIONS.REMOVE_CREDIT_MEMO, payload: { id } });
        }, []),

        updateCreditMemo: useCallback((memo) => {
            dispatch({ type: INVOICE_ACTIONS.UPDATE_CREDIT_MEMO, payload: memo });
        }, []),

        setCreditMemos: useCallback((memos) => {
            dispatch({ type: INVOICE_ACTIONS.SET_CREDIT_MEMOS, payload: memos });
        }, []),

        markCreditMemoForDeletion: useCallback((id) => {
            dispatch({ type: INVOICE_ACTIONS.MARK_CREDIT_MEMO_FOR_DELETION, payload: { id } });
        }, []),

        unmarkCreditMemoForDeletion: useCallback((id) => {
            dispatch({ type: INVOICE_ACTIONS.UNMARK_CREDIT_MEMO_FOR_DELETION, payload: { id } });
        }, []),

        // Invoice Meta
        setInvoiceMeta: useCallback((meta) => {
            dispatch({ type: INVOICE_ACTIONS.SET_INVOICE_META, payload: meta });
        }, []),

        updateCustomer: useCallback((customer) => {
            dispatch({ type: INVOICE_ACTIONS.UPDATE_CUSTOMER, payload: customer });
        }, []),

        // Full Reset/Set
        resetInvoice: useCallback(() => {
            dispatch({ type: INVOICE_ACTIONS.RESET_INVOICE });
        }, []),

        setFullInvoice: useCallback((invoiceData) => {
            dispatch({ type: INVOICE_ACTIONS.SET_FULL_INVOICE, payload: invoiceData });
        }, [])
    };

    return {
        invoiceState,
        dispatch,
        actions
    };
};

export default useInvoiceState;
